import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import Responsive from '../../../assets/config/Responsive'
import BankCardIcon from '../../../assets/icons/JSX/BankCardIcon'
import CloseIcon from '../../../assets/icons/JSX/CloseIcon'
import LeftLongArrow from '../../../assets/icons/JSX/LeftLongArrow'
import LoggedUserProfileIcon from '../../../assets/icons/JSX/LoggedUserProfileIcon'
import Logo from '../../../assets/icons/JSX/Logo'
import MenuIcon from '../../../assets/icons/JSX/MenuIcon'
import PlusIcon from '../../../assets/icons/JSX/PlusIcon'
import PolicyIcon from '../../../assets/icons/JSX/PolicyIcon'
import ProfileCircleIcon from '../../../assets/icons/JSX/ProfileCircleIcon'
import RightArrow from '../../../assets/icons/JSX/RightArrow'
import SecurityIcon from '../../../assets/icons/JSX/SecurityIcon'
import SignOutIcon from '../../../assets/icons/JSX/SignOutIcon'
import SmallArrow from '../../../assets/icons/JSX/SmallArrow'
import TransactionsIcon from '../../../assets/icons/JSX/TransactionsIcon'
import Colors from '../../../assets/styles/Colors'
import LangDropdown from '../../../components/LangDropdown'
import Loader from '../../../components/Loader'
import NotificationPanel from '../../../components/NotificationPanel'
import { RootState, useAppSelector } from '../../../store'
import { AuthState } from '../../../store/authSlice'
import api from '../../../api'
import { AppState, clearPhoneInfo } from '../../../store/appSlice'
import { useDispatch } from 'react-redux'
import IAIcon from '../../../assets/icons/JSX/AIIcon'

const LoggedUserHeader: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const [logout, { isLoading: logOutLoading }] = api.useLogoutMutation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [userDropdown, setUserDropdown] = useState<boolean>(false)
  const [openMobileMenu, setOpenMobileMenu] = useState<boolean>(false)
  const [openInnerMenu, setOpenInnerMenu] = useState<boolean>(false)
  const [showQuickNotifications, setShowQuickNotifications] = useState<boolean>(false)
  const btnRef = useRef(null)
  const { t } = useTranslation()

  useEffect(() => {
    const closeDropdown = (e: any) => {
      const path = e.path || (e.composedPath && e.composedPath())
      if (path[0] !== btnRef.current) {
        setUserDropdown(false)
      }
    }
    document.body.addEventListener('click', closeDropdown)
    return () => document.body.removeEventListener('click', closeDropdown)
  }, [])

  const Logout = async () => {
    try {
      await logout({}).unwrap()
      navigate(`/login`)
    } catch (error: any) {
      toast('logout error | ' + error)
    }
  }

  return logOutLoading ? (
    <Loader />
  ) : (
    <Wrapper style={style}>
      <Content>
        <UserName>
          {t('hi')} {lang === 'ge' ? auth?.user?.firstNameGe : auth?.user?.firstName}
        </UserName>
        <RightSide>
          <HeaderIconsBox>
            <LangDropdown />
          </HeaderIconsBox>
          <HeaderIconsBox>
            <MyAccount
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              ref={btnRef}
              onClick={() => setUserDropdown(!userDropdown)}
            >
              <LoggedUserProfileIconStyle />
              <span>{t('myAccount')}</span>
              <SmallArrowStyle style={{ transform: userDropdown ? 'rotate(0deg)' : 'rotate(180deg)' }} />
            </MyAccount>

            {userDropdown === true ? (
              <UserDropdownContent>
                <ul>
                  <li onClick={() => [dispatch(clearPhoneInfo()), navigate('/profile')]}>
                    <ProfileCircleIcon /> {t('profile')}
                  </li>
                  <li onClick={() => navigate('/security')}>
                    <SecurityIcon /> {t('security')}
                  </li>
                  <li onClick={Logout}>
                    <SignOutIcon /> {t('signOut')}
                  </li>
                </ul>
              </UserDropdownContent>
            ) : null}

            {showQuickNotifications ? (
              <NotificationsList
                onMouseEnter={() => setShowQuickNotifications(showQuickNotifications)}
                onMouseLeave={() => setShowQuickNotifications(!showQuickNotifications)}
              >
                <NotificationPanel />
              </NotificationsList>
            ) : null}
          </HeaderIconsBox>
        </RightSide>
      </Content>

      <MobileTabletContent>
        <MobileHeader>
          <LogoStyle onClick={() => navigate('/')} />
          <div>
            <MobileIconBtn onClick={() => setOpenMobileMenu(!openMobileMenu)}>
              <OpenMenuIcon />
            </MobileIconBtn>
          </div>
        </MobileHeader>
        <MobileMenuContent $openMobileMenu={openMobileMenu}>
          <Drawer>
            <LogoStyle onClick={() => navigate('/my-policies')} />
            <div>
              <MobileIconBtn onClick={() => setOpenMobileMenu(!openMobileMenu)}>
                <CloseIcon />
              </MobileIconBtn>
            </div>
          </Drawer>
          <MenuItems>
            <SideBarUl>
              <NewPolicyBtn onClick={() => navigate('/category')}>
                <PlusIcon /> <span>{t('newPolicy')}</span>
              </NewPolicyBtn>
              <Account>
                <SideBarLi
                  onClick={() => setOpenInnerMenu(!openInnerMenu)}
                  style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                >
                  <LoggedUserProfileIcon />
                  <span>{t('myAccount')}</span>{' '}
                  <div style={{ marginLeft: 'auto' }}>
                    <RightArrowStyle style={{ marginRight: '0px' }} />
                  </div>
                </SideBarLi>
              </Account>

              <SideBarLi onClick={() => [navigate('/my-policies'), setOpenMobileMenu(!openMobileMenu)]}>
                <PolicyIcon />
                <span>{t('myPolicies')}</span>
              </SideBarLi>
              <SideBarLi onClick={() => [navigate('/transactions'), setOpenMobileMenu(!openMobileMenu)]}>
                <TransactionsIcon />
                <span>{t('transactions')}</span>
              </SideBarLi>
              <SideBarLi
                style={{ position: 'relative' }}
                onClick={() => [navigate('/ai-assistent'), setOpenMobileMenu(!openMobileMenu)]}
              >
                <IAIcon />
                <BetaBadge>Beta</BetaBadge>
                <span>{t('ai_assistent')}</span>
              </SideBarLi>

              <TopBorderStyle>
                <LangDropdown />
              </TopBorderStyle>

              <SideBarLiWithBorder onClick={Logout}>
                <SignOutIcon />
                <span>{t('signOut')}</span>
              </SideBarLiWithBorder>
            </SideBarUl>
          </MenuItems>

          <InnerMenu $openInnerMenu={openInnerMenu}>
            <InnerMenuHead onClick={() => setOpenInnerMenu(!openInnerMenu)}>
              <InnerMenuBtn>
                <LeftLongArrow />
                უკან
              </InnerMenuBtn>
            </InnerMenuHead>
            <InnerMenuContent>
              <SideBarUl>
                <SideBarLi
                  onClick={() => [
                    dispatch(clearPhoneInfo()),
                    navigate('/profile'),
                    setOpenInnerMenu(!openInnerMenu),
                    setOpenMobileMenu(!openMobileMenu),
                  ]}
                >
                  <ProfileCircleIcon />
                  <span>{t('profile')}</span>
                </SideBarLi>
                <SideBarLi onClick={() => navigate('/security')}>
                  <SecurityIcon />
                  <span>{t('security')}</span>
                </SideBarLi>
                <SideBarLi
                  onClick={() => [
                    navigate('/transactions'),
                    setOpenInnerMenu(!openInnerMenu),
                    setOpenMobileMenu(!openMobileMenu),
                  ]}
                >
                  <BankCardIcon />
                  <span>{t('transactions')}</span>
                </SideBarLi>
              </SideBarUl>
            </InnerMenuContent>
          </InnerMenu>
        </MobileMenuContent>
      </MobileTabletContent>
    </Wrapper>
  )
}

const SmallArrowStyle = styled(SmallArrow)`
  pointer-events: none;
`
const MyAccount = styled.div`
  user-select: none;
  /* my account text */
  span {
    font-size: 14px;
    margin-left: 14px;
    margin-right: 10px;
    pointer-events: none;
  }
`
const TopBorderStyle = styled.div`
  border-top: 1px solid ${Colors.secondaryGrey};
  padding-top: 20px;
  padding-bottom: 10px;
`
const InnerMenuContent = styled.div`
  padding: 0px 25px;
  margin-top: 25px;
`
const InnerMenuBtn = styled.button`
  border: 0px;
  outline: none;
  background-color: transparent;
  font-size: 14px;
  color: ${Colors.primaryBlack};

  /* inner svg */
  svg {
    margin-right: 10px;
    width: 18px;
    margin-top: -2px;
  }
`
const InnerMenuHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 65px;
  padding: 0px 25px;
  box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  -webkit-box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  -moz-box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
`
const InnerMenu = styled.div<{ $openInnerMenu: boolean }>`
  position: fixed;
  top: 0px;
  right: ${({ $openInnerMenu }) => ($openInnerMenu === true ? '0' : '-200vw')};
  transition: all 200ms ease-in-out;
  z-index: 9;
  background-color: ${Colors.white};
  width: 100%;
  min-height: 100dvh;
`
const RightArrowStyle = styled(RightArrow)`
  height: 11px !important;
`
const Account = styled.div`
  margin-top: 35px;
  border-bottom: 1px solid ${Colors.secondaryGrey};
`
const SideBarLi = styled.li`
  padding-top: 10px;
  padding-bottom: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  color: ${Colors.primaryGrey};

  /* side bar li text */
  span {
    font-size: 14px;
    line-height: 16px;
  }

  /* sibar bar li icons */
  svg {
    color: ${Colors.primaryGrey};
    width: 18px;
    height: 18px;
    margin-right: 18px;
    margin-top: -3px;
  }

  /* link & a */
  a {
    color: ${Colors.primaryGrey};
    text-decoration: none;
  }

  /* hover */
  &:hover {
    color: ${Colors.primaryBlack};
    svg {
      color: ${Colors.primaryBlack};
    }
    a {
      color: ${Colors.primaryBlack};
    }
  }
`
const SideBarUl = styled.ul`
  list-style: none;
  padding-left: 0px;
`
const SideBarLiWithBorder = styled(SideBarLi)`
  border-top: 1px solid ${Colors.secondaryGrey};
  padding-top: 20px;
`
const NewPolicyBtn = styled.button`
  width: 100%;
  border: 0px;
  outline: none;
  border-radius: 4px;
  background-color: ${Colors.primaryRed};
  color: ${Colors.white};
  height: 48px;
  margin-top: 20px;
  font-size: 14px;
  svg {
    transform: scale(1.05);
    width: 10px;
    margin-right: 8px;
    margin-top: -3px;
    color: ${Colors.white};
  }
`
const Drawer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 65px;
  padding: 0px 25px;
  box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  -webkit-box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  -moz-box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
`
const MenuItems = styled.div`
  padding: 0px 25px 50px 25px;
  overflow-y: scroll;
  height: calc(100dvh - 65px);
`
const MobileIconBtn = styled.button`
  margin-left: 20px;
  border: 0px;
  outline: none;
  background-color: transparent;
  color: ${Colors.primaryBlack};
`
const OpenMenuIcon = styled(MenuIcon)``
const LogoStyle = styled(Logo)`
  width: 60px;
`
const MobileMenuContent = styled.div<{ $openMobileMenu: boolean }>`
  position: fixed;
  top: 0px;
  right: ${({ $openMobileMenu }) => ($openMobileMenu === true ? '0' : '-200vw')};
  transition: all 200ms ease-in-out;
  background-color: ${Colors.white};
  width: 100%;
  min-height: 100dvh;
  z-index: 20;
`
const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 65px;
`
const MobileTabletContent = styled.div`
  display: none;
  background-color: ${Colors.white};
  height: 65px;
  width: 100%;
  padding: 0px 25px;
  box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  -webkit-box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  -moz-box-shadow: 0px 1px 12px 1px rgba(118, 131, 139, 0.23);
  ${Responsive.tablet} {
    display: block;
  }
  ${Responsive.mobile} {
    display: block;
  }
`
const LoggedUserProfileIconStyle = styled(LoggedUserProfileIcon)`
  user-select: none;
  pointer-events: none;
  margin-top: -1px;
`
const UserDropdownContent = styled.div`
  position: absolute;
  top: 40px;
  right: -3px;
  min-width: 150px;
  text-align: left;
  padding: 15px 24px 15px 20px;
  background-color: ${Colors.white};
  border-radius: 4px;
  box-shadow: 0px 1px 5px -2px rgba(0, 0, 0, 0.41);
  -webkit-box-shadow: 0px 1px 5px -2px rgba(0, 0, 0, 0.41);
  -moz-box-shadow: 0px 1px 5px -2px rgba(0, 0, 0, 0.41);
  z-index: 5;

  /* user dropdown ul */
  ul {
    padding-left: 0px;
    list-style: none;
    margin-bottom: 0px;
  }

  /* user dropdown li */
  li {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 16px;
    line-height: 16px;
    margin: 10px 0px;
    padding: 8px 0px;
    cursor: pointer;
    color: ${Colors.primaryGrey};
    /*  */
    svg {
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }
    /*  */
    &:hover {
      color: ${Colors.primaryBlack};
    }
  }
`
// const RedDot = styled.div`
//   width: 5px;
//   height: 5px;
//   border-radius: 50%;
//   background-color: ${Colors.primaryRed};
//   position: absolute;
//   top: 1px;
//   right: -2px;
// `
const HeaderIconsBox = styled.div`
  padding-left: 30px;
  margin-left: 30px;
  border-left: 1px solid ${Colors.secondaryGrey};
  height: 24px;
  position: relative;

  &:first-child {
    border: 0px;
    cursor: pointer;
  }
`
const RightSide = styled.div`
  display: flex;
  align-items: center;
`
const UserName = styled.div`
  font-size: 36px;
  color: ${Colors.primaryBlack};
  line-height: 25px;
`
const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${Responsive.tablet} {
    display: none;
  }
  ${Responsive.mobile} {
    display: none;
  }
`
const Wrapper = styled.div`
  /* background-color: red; */
`
const NotificationsList = styled.div`
  position: absolute;
  top: 26px;
  right: 90px;
  width: 300px;
  text-align: left;
  padding: 30px 40px 60px 50px;
  background-color: ${Colors.white};
  border-radius: 10px;
  box-shadow: 0px 1px 5px -2px rgba(0, 0, 0, 0.41);
  -webkit-box-shadow: 0px 1px 5px -2px rgba(0, 0, 0, 0.41);
  -moz-box-shadow: 0px 1px 5px -2px rgba(0, 0, 0, 0.41);
  z-index: 5;
`
const BetaBadge = styled.span`
  position: absolute;
  top: 15px;
  right: 2px;
  background-color: ${Colors.primaryRed};
  color: ${Colors.white};
  font-size: 12px !important;
  font-weight: 600;
  padding: 4px 7px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 10px !important;
  z-index: 1;
  pointer-events: none;
  user-select: none;
`
//
//
//
export default LoggedUserHeader
